/**
 * What a receipt's status means for the screen showing it.
 *
 * Extraction is observed through the receipt itself (CLAUDE.md, "Async work"): there is no jobs
 * endpoint, so the detail page polls `GET /receipts/{id}` while the status says the worker is
 * busy, and stops the moment it says anything else.
 */

import type { components } from "@/api/schema";

export type ReceiptStatus = components["schemas"]["Status"];

/** Whether waiting longer could change this receipt without the user doing anything. */
export function isWorking(status: ReceiptStatus | undefined): boolean {
  return status === "extracting";
}

/** Brisk at first, because extraction usually finishes within seconds. */
export const FAST_POLL_MS = 2_000;
/** After a minute something is slow -- a busy worker, a stopped one -- and hammering helps nobody. */
export const SLOW_POLL_MS = 10_000;
export const FAST_POLLS = 30;

/**
 * How long to wait before asking again, or `false` to stop.
 *
 * `polls` is how many times the receipt has been fetched so far. Polling never stops on time
 * alone: a stopped worker will be restarted, and the receipt should finish on screen when it is,
 * without the user knowing to reload.
 */
export function pollInterval(status: ReceiptStatus | undefined, polls: number): number | false {
  if (!isWorking(status)) return false;
  return polls < FAST_POLLS ? FAST_POLL_MS : SLOW_POLL_MS;
}

/** Long enough that the user deserves to be told it is slow rather than left wondering. */
export const SLOW_AFTER_MS = 60_000;
